import React, { useMemo } from 'react';

import { Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';

import { skipToken } from '@reduxjs/toolkit/query/react';

import { useGetDownloadURL } from '../../../../redux/api/fireBaseStorageApi';
import { AvatarSize } from '../../../../shared/constants';
import AppAvatar, { IAppAvatarProps } from './../app-avatar/AppAvatar';
import { useStyles } from './AppEntityAvatar.styles';

export interface AppEntityAvatarProps extends IAppAvatarProps {
  fileBlob?: string;
  storageLink?: string;
  previewStorageLink?: string;
  directStorageLink?: string;
  directPreviewStorageLink?: string;
  size?: AvatarSize;
  border?: 'primary' | 'secondary' | 'white' | 'none';
}

function getStorageLink(
  size: AvatarSize,
  previewStorageLink?: string,
  storageLink?: string,
) {
  if (
    (size === AvatarSize.xs ||
      size === AvatarSize.s ||
      size === AvatarSize.m) &&
    previewStorageLink
  )
    return previewStorageLink;

  return storageLink;
}

function getDirectLink(
  size: AvatarSize,
  directPreviewStorageLink?: string,
  directStorageLink?: string,
) {
  if (
    (size === AvatarSize.xs ||
      size === AvatarSize.s ||
      size === AvatarSize.m) &&
    directPreviewStorageLink
  )
    return directPreviewStorageLink;

  return directStorageLink;
}

export default function AppEntityAvatar(props: AppEntityAvatarProps) {
  const {
    fileBlob,
    storageLink,
    previewStorageLink,
    directStorageLink,
    directPreviewStorageLink,
    size = AvatarSize.m,
    border = 'none',
    children,
    ...avatarProps
  } = props;

  const theme = useTheme();
  const { classes } = useStyles(theme);

  const effectiveLink = useMemo(
    () =>
      fileBlob
        ? undefined
        : getStorageLink(size, previewStorageLink, storageLink),
    [fileBlob, size, previewStorageLink, storageLink],
  );
  const effectiveDirectLink = useMemo(
    () =>
      fileBlob
        ? undefined
        : getDirectLink(size, directPreviewStorageLink, directStorageLink),
    [fileBlob, size, directPreviewStorageLink, directStorageLink],
  );

  const { data: fetchedUrl, isLoading } = useGetDownloadURL(
    effectiveLink || effectiveDirectLink
      ? {
          storageLink: effectiveLink,
          directLink: effectiveDirectLink,
        }
      : skipToken,
  );

  const avatarSrc =
    fileBlob ?? (effectiveLink || effectiveDirectLink ? fetchedUrl : undefined);

  const loaderSpinnerSize = Math.max(10, Math.round(size * 0.7));

  return (
    <div className={classes.root}>
      {!fileBlob && isLoading ? (
        <div
          className={classes.loader}
          style={{ width: `${size}px`, height: `${size}px` }}>
          <CircularProgress
            color="primary"
            size={loaderSpinnerSize}
            thickness={4}
          />
        </div>
      ) : (
        <AppAvatar src={avatarSrc} size={size} border={border} {...avatarProps}>
          <Box>{children}</Box>
        </AppAvatar>
      )}
    </div>
  );
}
